import type {
  DividendCurrencyCode,
  DividendDatesApiData,
  DividendInfoApiData,
  DividendNumeric,
  DividendStockPriceHistoryCsv,
  DividendTransitionApiData,
  DividendTransitionParentApiRow,
  DividendTransitionQuarterApiRow,
} from '@app-types/dividendApi';
import {
  buildStockCloseByMonth,
  parseDividendStockPriceCsv,
} from './dividendStockPriceCsv';
import type {
  DividendChartData,
  DividendInfoBlockData,
  DividendMatrixTableData,
  DividendTableCell,
  DividendTableRow,
  DividendTrendsSectionData,
} from '@app-types/performanceDividend';
import { PLACEHOLDER } from '@utils/format';

// ---- Numeric / text normalization ----------------------------------

/** Accepts numbers or numeric strings; returns null for missing/invalid values. */
const toNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumeric = (value: number): string =>
  value.toLocaleString('ja-JP', { maximumFractionDigits: 2 });

// Preserve the significant digits of API amounts, including small dividends
// that a fixed decimal-place limit would round to zero. Percentages keep
// their existing formatter above.
const formatAmount = (value: number): string =>
  value.toLocaleString('ja-JP', { maximumSignificantDigits: 21 });

/**
 * Screen-level display unit for Dividend monetary text. `USD` (case-insensitive,
 * trimmed) renders `ドル`; `JPY`, missing, null, blank, or unknown values keep
 * the established `円` display. This is display-only; numeric API values are
 * already expressed in the selected currency and are never converted.
 */
type DividendCurrencyDisplay = {
  unit: string;
};

const getDividendCurrencyDisplay = (
  currency: DividendCurrencyCode | null | undefined,
): DividendCurrencyDisplay => {
  const normalizedCurrency = String(currency ?? '').trim().toUpperCase();
  return normalizedCurrency === 'USD' ? { unit: 'ドル' } : { unit: '円' };
};

/** Per-share amounts with the screen currency unit appended (no rescaling). */
const formatDividendAmount = (
  value: number | string | null | undefined,
  unit: string,
): string => {
  const num = toNumber(value);
  return num === null ? PLACEHOLDER : `${formatAmount(num)}${unit}`;
};

/** Plain amount without unit (columns that already carry the 円 label). */
const formatPlainAmount = (value: number | string | null | undefined): string => {
  const num = toNumber(value);
  return num === null ? PLACEHOLDER : formatAmount(num);
};

/** Values already expressed as percent (e.g. 28.15 -> "28.15%"). */
const formatAlreadyPercent = (value: number | string | null | undefined): string => {
  const num = toNumber(value);
  return num === null ? PLACEHOLDER : `${formatNumeric(num)}%`;
};

/** Ratio values converted to percent (e.g. 0.03 -> "3%", 1.05 -> "105%"). */
const formatRatioAsPercent = (value: number | string | null | undefined): string => {
  const num = toNumber(value);
  if (num === null) return PLACEHOLDER;
  // Round to 2 decimals to avoid float artefacts (0.03 * 100 = 3.0000000000000004).
  const percent = Math.round(num * 100 * 100) / 100;
  return `${formatNumeric(percent)}%`;
};

/** Preserves an already-formatted date string; null/empty -> placeholder. */
const formatDate = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : PLACEHOLDER;
};

/** Date-only from a possibly date+time string (e.g. "2026/04/27 07:50:24"). */
const formatDateOnly = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) return PLACEHOLDER;
  const datePart = trimmed.split(/[ T]/)[0];
  return datePart || PLACEHOLDER;
};

/** Removes any forecast marker; the confirmed Dividend API has no forecast data. */
const stripForecast = (value: string | null | undefined): string => {
  const cleaned = (value ?? '').replace(/[（(]?\s*予\s*[)）]?/g, '').trim();
  return cleaned || PLACEHOLDER;
};

/** BA suffixes depend on the instrument market, independently of currency. */
const formatPeriodLabel = (
  row: DividendTransitionQuarterApiRow,
  market: string | null | undefined,
): string => {
  const period = stripForecast(row.period);
  const isUs = market === 'US';
  if (row.type_market === 'Special') return `${period}${isUs ? '(SP)' : '(特別)'}`;
  if (row.type_market === 'Capital gains, long-term') {
    return `${period}${isUs ? '(CG)' : '(C/G)'}`;
  }
  return period;
};

/** Stable payment identity, with a local occurrence suffix for repeated records. */
const createRowKey = (prefix: string) => {
  const occurrences = new Map<string, number>();
  return (row: DividendTransitionQuarterApiRow): string => {
    const identity = JSON.stringify([
      row.period, row.recordDate, row.period_type, row.type_market ?? null,
    ]);
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return `${prefix}-${identity}-${occurrence}`;
  };
};

// ---- Incomplete annual-parent detection -----------------------------

/** Numeric field carries data when it normalizes to a finite number (0 counts). */
const hasNumericData = (value: DividendNumeric): boolean => toNumber(value) !== null;

/** Date/string field carries data when it is non-empty after trimming. */
const hasTextData = (value: string | null | undefined): boolean =>
  Boolean(value?.trim());

/**
 * True when a transition parent has at least one displayable annual field, so
 * it should render as a normal grey annual row. A parent whose seven displayed
 * annual fields (`dps`, `annualDps`, `payoutRatio`, `recordDate`, `exDate`,
 * `payDate`, `updateDate`) are all no-data is an incomplete annual placeholder:
 * it is hidden as an annual row while its interim children stay visible.
 *
 * `period`/`period_type`/`priceAsOfRecordDate`/`quarter` are intentionally not
 * used here. Zero is valid data (matches the table formatters).
 */
const hasAnnualDisplayData = (
  parent: DividendTransitionParentApiRow,
): boolean =>
  hasNumericData(parent.dps) ||
  hasNumericData(parent.annualDps) ||
  hasNumericData(parent.payoutRatio) ||
  hasTextData(parent.recordDate) ||
  hasTextData(parent.exDate) ||
  hasTextData(parent.payDate) ||
  hasTextData(parent.updateDate);

// ---- `配当情報` ------------------------------------------------------

type DividendInfoFormatter = (data: DividendInfoApiData, unit: string) => string;

const DIVIDEND_INFO_FORMATTERS: Record<string, DividendInfoFormatter> = {
  actualDividendAmount: (data, unit) => formatDividendAmount(data.actualDps, unit),
  forecastDividendAmount: (data, unit) => formatDividendAmount(data.estimateDps, unit),
  forecastDividendYield: (data) => formatAlreadyPercent(data.estimateDividendYield),
  payoutRatio: (data) => formatAlreadyPercent(data.payoutRatio),
  doe: (data) => formatRatioAsPercent(data.doe),
};

/**
 * Maps `/dividend-info` values onto the static info block. Static copy (title,
 * node IDs, labels) is preserved from `fallback`; missing API data renders `--`
 * instead of mock dividend values. The BA-approved screen currency
 * (`apiData.currency`) sets the per-share unit; yield/payout/DOE stay percent.
 */
export const mapDividendInfoApiToBlock = (
  apiData: DividendInfoApiData | null | undefined,
  fallback: DividendInfoBlockData,
): DividendInfoBlockData => {
  const { unit } = getDividendCurrencyDisplay(apiData?.currency);
  return {
    ...fallback,
    rows: fallback.rows.map((row) => {
      const formatter = DIVIDEND_INFO_FORMATTERS[row.key];
      return {
        ...row,
        value: apiData && formatter ? formatter(apiData, unit) : PLACEHOLDER,
      };
    }),
  };
};

// ---- `関連スケジュール` ---------------------------------------------

const DIVIDEND_DATES_FIELDS: Record<string, keyof DividendDatesApiData> = {
  lastTradingDateWithRights: 'lastRightDate',
  exDividendDate: 'exDate',
  recordDate: 'recordDate',
  paymentDate: 'payDate',
};

/**
 * Maps `/dividend-dates` values onto the static related-schedule block.
 * `payDate: null` renders `--`.
 */
export const mapDividendDatesApiToBlock = (
  apiData: DividendDatesApiData | null | undefined,
  fallback: DividendInfoBlockData,
): DividendInfoBlockData => ({
  ...fallback,
  rows: fallback.rows.map((row) => {
    const field = DIVIDEND_DATES_FIELDS[row.key];
    const raw = apiData && field ? apiData[field] : null;
    return { ...row, value: formatDate(raw) };
  }),
});

// ---- `配当推移` (annual / quarterly transition) ---------------------

const cell = (text: string): DividendTableCell => ({ text, nowrap: true });

/** Comparable numeric key (YYYYMMDD) from recordDate, falling back to period. */
const parseDateValue = (row: DividendTransitionQuarterApiRow): number => {
  const source = (row.recordDate || row.period || '').trim();
  if (!source) return 0;
  const [y, m, d] = source.split('/');
  return (Number(y) || 0) * 10000 + (Number(m) || 0) * 100 + (Number(d) || 0);
};

const buildTransitionCells = (
  row: DividendTransitionQuarterApiRow,
  annualDps: DividendNumeric,
  unit: string,
  showAnnualMetrics = true,
): DividendTableCell[] => [
  cell(formatDividendAmount(row.dps, unit)),
  cell(showAnnualMetrics ? formatDividendAmount(annualDps, unit) : PLACEHOLDER),
  // `/dividend-transition` now returns payoutRatio already as a percent
  // (e.g. 28.29 -> "28.29%"); do not multiply by 100.
  cell(showAnnualMetrics ? formatAlreadyPercent(row.payoutRatio) : PLACEHOLDER),
  cell(formatDate(row.recordDate)),
  cell(formatDate(row.exDate)),
  cell(formatDate(row.payDate)),
  cell(formatDateOnly(row.updateDate)),
];

const buildHistoryCells = (
  row: DividendTransitionParentApiRow,
): DividendTableCell[] => [
  cell(formatPlainAmount(row.dps)),
  cell(formatPlainAmount(row.annualDps)),
  cell(formatDate(row.recordDate)),
  cell(formatDate(row.exDate)),
  cell(formatDate(row.payDate)),
  cell(formatDateOnly(row.updateDate)),
];

/** Display-only US projection; raw values and annual eligibility stay intact. */
const withoutPayoutColumn = (table: DividendMatrixTableData): DividendMatrixTableData => {
  const columnIndex = table.columns.findIndex((column) => column.key === 'payoutRatio');
  if (columnIndex < 1) return table;

  const configuredWidth = table.columns[columnIndex].width;
  const removedWidth = typeof configuredWidth === 'number' && Number.isFinite(configuredWidth)
    ? Math.max(0, configuredWidth)
    : 0;
  const reduceWidth = (width: number | undefined): number | undefined =>
    typeof width === 'number' && Number.isFinite(width) ? Math.max(0, width - removedWidth) : undefined;
  const projectRow = (row: DividendTableRow): DividendTableRow => ({
    ...row,
    cells: row.cells.filter((_, index) => index !== columnIndex - 1),
    children: row.children?.map(projectRow),
  });

  return {
    ...table,
    columns: table.columns.filter((_, index) => index !== columnIndex),
    rows: table.rows.map(projectRow),
    minWidth: reduceWidth(table.minWidth),
    mobileMinWidth: reduceWidth(table.mobileMinWidth),
  };
};

/** "2026/03" -> "26/03" for the compact chart x-axis. */
const toShortChartLabel = (period: string | null | undefined): string => {
  const [y, m] = (period ?? '').split('/');
  if (!y || !m) return period ?? '';
  return `${y.slice(-2)}/${m}`;
};

const niceStep = (rough: number): number => {
  if (rough <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / pow;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * pow;
};

const computeAxis = (
  values: number[],
  fallbackMax: number,
  fallbackInterval: number,
): { max: number; interval: number } => {
  const positive = values.filter((value) => value > 0);
  if (!positive.length) return { max: fallbackMax, interval: fallbackInterval };
  const max = Math.max(...positive);
  const interval = niceStep(max / 4);
  return { max: Math.ceil(max / interval) * interval, interval };
};

const sortByDateDesc = <T extends DividendTransitionQuarterApiRow>(rows: T[]): T[] =>
  [...rows].sort((a, b) => parseDateValue(b) - parseDateValue(a));

const normalizeYears = (years: number): number =>
  Number.isFinite(years) && years > 0 ? years : 10;

/** Filter before the 5/10 cap so other payments never consume chart periods. */
const selectChartParents = (
  parents: DividendTransitionParentApiRow[],
  years: number,
): DividendTransitionParentApiRow[] =>
  sortByDateDesc(parents)
    .filter((parent) => parent.type_market === 'Final' && hasAnnualDisplayData(parent))
    .slice(0, normalizeYears(years));

// ---- Stock-price line (chart5-hts CSV) date alignment ---------------

/** `2026/3` / `2026/03/31` -> zero-padded parts, or null when unparseable. */
const splitDateParts = (
  value: string | null | undefined,
): { year: string; month: string; day?: string } | null => {
  const parts = (value ?? '').trim().split('/');
  const [y, m, d] = parts;
  if (!y || !m) return null;
  const year = y.padStart(4, '0');
  const month = m.padStart(2, '0');
  const day = d ? d.padStart(2, '0') : undefined;
  return { year, month, day };
};

/** Last calendar day (DD) of a given year/month. */
const lastDayOfMonth = (year: string, month: string): string => {
  const day = new Date(Number(year), Number(month), 0).getDate();
  return String(day).padStart(2, '0');
};

/**
 * Chart-aligned trade date (YYYYMMDD) for an annual row. The annual x-axis and
 * red bars are keyed on `parent.period`, so Chart5 alignment must prefer the
 * period month-end (e.g. `2025/09` -> `20250930`), falling back to a full
 * `recordDate`, then `exDate`, only when `period` is missing/unusable. Returns
 * null when no usable date can be derived. `priceAsOfRecordDate` is never used.
 */
const deriveAnnualChartDate = (
  row: DividendTransitionParentApiRow,
): string | null => {
  const periodParts = splitDateParts(row.period);
  if (periodParts) {
    const day = periodParts.day ?? lastDayOfMonth(periodParts.year, periodParts.month);
    return `${periodParts.year}${periodParts.month}${day}`;
  }
  const recordParts = splitDateParts(row.recordDate);
  if (recordParts?.day) {
    return `${recordParts.year}${recordParts.month}${recordParts.day}`;
  }
  const exParts = splitDateParts(row.exDate);
  if (exParts?.day) {
    return `${exParts.year}${exParts.month}${exParts.day}`;
  }
  return null;
};

/** Year+month bucket (YYYYMM) used to align an annual row with CSV rows. */
const deriveAnnualMonthKey = (
  row: DividendTransitionParentApiRow,
): string | null => {
  const date = deriveAnnualChartDate(row);
  return date ? date.slice(0, 6) : null;
};

/**
 * Derives the stock-price API date range from the visible annual chart rows.
 * Per the Chart5 request contract `from` is the oldest visible annual date and
 * `to` is the newest, both YYYYMMDD. Dates come from the period-first
 * `deriveAnnualChartDate` helper so range and CSV month matching stay aligned.
 * Returns null when a range cannot be derived (no data / undatable rows).
 */
export const getDividendStockPriceDateRange = (
  apiData: DividendTransitionApiData | null | undefined,
  options: { years: number },
): { from: string; to: string } | null => {
  const years = normalizeYears(options.years);
  const parentAll: DividendTransitionParentApiRow[] = Array.isArray(apiData)
    ? apiData.filter(
        (parent): parent is DividendTransitionParentApiRow => Boolean(parent),
      )
    : [];
  if (!parentAll.length) return null;

  // Use the same Final-only selection as the chart, including its 5/10 cap.
  const visibleDesc = selectChartParents(parentAll, years);
  const dates = visibleDesc
    .map((row) => deriveAnnualChartDate(row))
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => a.localeCompare(b));
  if (!dates.length) return null;

  // Chart5 expects chronological bounds: oldest date in `from`, newest in `to`.
  return { from: dates[0], to: dates[dates.length - 1] };
};


/**
 * Maps `/dividend-transition` grouped data into the trends view model:
 * - grey parent rows from the response data array
 * - white child rows directly from each parent's `quarter` array
 * - a chart from parent `annualDps` values (ascending)
 * - a flat latest-4 parent bottom history table
 *
 * Visible annual periods are defensively capped to `options.years`. Missing API
 * data yields empty tables/chart instead of mock dividend values.
 */
export const mapDividendTransitionApiToTrends = (
  apiData: DividendTransitionApiData | null | undefined,
  fallback: DividendTrendsSectionData,
  options: {
    years: number;
    stockPriceCsv?: DividendStockPriceHistoryCsv | null;
    companyName?: string | null;
    currency?: DividendCurrencyCode | null;
    /** Instrument `mkt` for Special/capital-gains labels; Japanese by default. */
    market?: string | null;
  },
): DividendTrendsSectionData => {
  const years = normalizeYears(options.years);
  // Screen-level display currency from `/dividend-info`. Display-only: it sets
  // per-share/annual units, history headers, and chart axis labels without
  // converting numeric values or refetching transition/Chart5 data.
  const { unit } = getDividendCurrencyDisplay(options.currency);

  // Blue stock series/legend use the response `nameJp`. A missing/blank name
  // renders `--` instead of the hard-coded mock label (e.g. コマツ); the red
  // dividend series/legend stay untouched.
  const normalizedCompanyName =
    typeof options.companyName === 'string' && options.companyName.trim()
      ? options.companyName.trim()
      : PLACEHOLDER;
  const parentAll: DividendTransitionParentApiRow[] = Array.isArray(apiData)
    ? apiData.filter(
        (parent): parent is DividendTransitionParentApiRow => Boolean(parent),
      )
    : [];

  const parentDesc = sortByDateDesc(parentAll);
  // Complete annual parents drive table slot counting and retained history.
  // Incomplete annual placeholders keep their interim children but never
  // consume a 5/10 annual slot.
  const annualParentDesc = parentDesc.filter(hasAnnualDisplayData);
  const chartParentDesc = selectChartParents(parentAll, years);

  // Sorted child rows for a container; `keyPrefix` keeps keys unique whether the
  // children are nested under a grey parent or flattened as direct white rows.
  const buildChildRows = (
    parent: DividendTransitionParentApiRow,
    keyPrefix: string,
  ): DividendTableRow[] => {
    const quarterRows: DividendTransitionQuarterApiRow[] = Array.isArray(
      parent.quarter,
    )
      ? parent.quarter.filter(
          (quarter): quarter is DividendTransitionQuarterApiRow =>
            Boolean(quarter),
        )
      : [];
    const childKey = createRowKey(`${keyPrefix}-child`);
    return sortByDateDesc(quarterRows).map((quarter) => ({
      key: childKey(quarter),
      label: formatPeriodLabel(quarter, options.market),
      variant: 'default' as const,
      cells: buildTransitionCells(quarter, null, unit, false),
    }));
  };

  // --- trends table (API-grouped parents with direct nested children) ---
  // Walk descending: complete parents render as grey rows (consuming an annual
  // slot); incomplete placeholders flatten their children as direct white rows
  // in place without consuming a slot. Stop once the annual window is filled.
  const trendRows: DividendTableRow[] = [];
  const annualKey = createRowKey('annual');
  let annualShown = 0;
  for (const parent of parentDesc) {
    const isCompleteAnnual = hasAnnualDisplayData(parent);
    if (isCompleteAnnual && annualShown >= years) break;

    const parentKey = annualKey(parent);

    if (!isCompleteAnnual) {
      // Incomplete annual placeholder: surface its interim children directly,
      // without a grey parent row or disclosure control.
      trendRows.push(...buildChildRows(parent, parentKey));
      continue;
    }

    const children = buildChildRows(parent, parentKey);
    trendRows.push({
      key: parentKey,
      label: formatPeriodLabel(parent, options.market),
      variant: 'header-like' as const,
      disclosure: children.length > 0 ? 'down' : undefined,
      children: children.length ? children : undefined,
      cells: buildTransitionCells(parent, parent.annualDps, unit),
    });
    annualShown += 1;
  }

  const completeTrendsTable: DividendMatrixTableData = {
    ...fallback.trendsTable,
    rows: trendRows,
  };
  const trendsTable = options.market === 'US'
    ? withoutPayoutColumn(completeTrendsTable)
    : completeTrendsTable;

  // --- bottom history table (flat latest 4 complete annual rows) ---
  const historyKey = createRowKey('history');
  const historyRows: DividendTableRow[] = annualParentDesc
    .slice(0, 4)
    .map((parent) => ({
      key: historyKey(parent),
      label: formatPeriodLabel(parent, options.market),
      variant: 'header-like' as const,
      cells: buildHistoryCells(parent),
    }));

  // Clone (never mutate) fallback columns and apply the screen currency unit to
  // the two monetary headers only. History amount cells stay unitless because
  // the unit is expressed through these column headers.
  const historyColumns = fallback.historyTable.columns.map((column) => {
    if (column.key === 'perShare') {
      return { ...column, label: [`一株配当(${unit})`] };
    }
    if (column.key === 'annual') {
      return { ...column, label: [`年間配当(${unit})`] };
    }
    return column;
  });

  const historyTable: DividendMatrixTableData = {
    ...fallback.historyTable,
    columns: historyColumns,
    rows: historyRows,
  };

  // --- chart (annual data only, ascending) ---
  // Blue stock-price line comes from the chart5-hts CSV, matched to each
  // visible annual row by year+month. `priceAsOfRecordDate` is intentionally
  // not used, even when the transition response provides a value.
  const stockCloseByMonth = buildStockCloseByMonth(
    parseDividendStockPriceCsv(options.stockPriceCsv),
  );
  const chartParentAsc = [...chartParentDesc].sort(
    (a, b) => parseDateValue(a) - parseDateValue(b),
  );
  const points = chartParentAsc.map((parent) => {
    const monthKey = deriveAnnualMonthKey(parent);
    const stockPrice =
      monthKey && stockCloseByMonth.has(monthKey)
        ? stockCloseByMonth.get(monthKey)!
        : null;
    return {
      label: toShortChartLabel(parent.period),
      dividend: toNumber(parent.annualDps),
      stockPrice,
    };
  });

  const dividendValues = points
    .map((point) => point.dividend)
    .filter((value): value is number => value !== null);
  const stockValues = points
    .map((point) => point.stockPrice)
    .filter((value): value is number => value !== null);

  const leftAxis = computeAxis(
    dividendValues,
    fallback.chart.leftAxisMax,
    fallback.chart.leftAxisInterval,
  );
  const rightAxis = computeAxis(
    stockValues,
    fallback.chart.rightAxisMax,
    fallback.chart.rightAxisInterval,
  );

  const chart: DividendChartData = {
    ...fallback.chart,
    yAxisLeftLabel: `配当(${unit})`,
    yAxisRightLabel: `株価(${unit})`,
    stockSeriesName: normalizedCompanyName,
    leftAxisMax: leftAxis.max,
    leftAxisInterval: leftAxis.interval,
    rightAxisMin: 0,
    rightAxisMax: rightAxis.max,
    rightAxisInterval: rightAxis.interval,
    points,
  };

  // Mirror the blue stock series label on the external `stock` legend item
  // without mutating the fallback legend. The red `dividend` item is unchanged.
  const legend = fallback.legend.map((item) =>
    item.key === 'stock' ? { ...item, label: normalizedCompanyName } : item,
  );

  return {
    ...fallback,
    chart,
    legend,
    trendsTable,
    historyTable,
  };
};
